/**
 * One entry as a SECTION of a page — the shape both page kinds share since
 * 2026-09-28 (Teo: "same ui/ux for web as for cad page"): a KingCAD layer on
 * its folder page (`#sloj-<xml_id>`) and a web product on its theme page
 * (`#sloj-<key>`). Title (an anchor link), the chips, the other language's
 * title, the parent line, the text (or the king admin's edit form), the entry's
 * own facts, its links, related entries, components and, for a king admin, the
 * edit history. Every section carries its FULL facts card — the »Vir« row
 * included; nothing is hoisted into a shared page head (Teo 2026-09-28: "each
 * layer has full data for itself … no common denominators").
 *
 * Text leads in the page's language (docsText.ts): on /cz/docs a bilingual
 * entry shows its Czech title and text first with the Slovenian as the labelled
 * secondary block; on /si/docs the other way round.
 */

import type { WikiEntry, WikiSection } from '../../../shared/types/layerWiki.js';
import { docsCopy, getDocsLanguage } from '../docsCopy.js';
import type { EditState } from '../docsEdit.js';
import {
  buildComponents,
  buildFacts,
  buildLinks,
  buildRelations,
  hasDescription,
  inheritDates,
  surfaceChip,
  type FactRow,
} from '../docsFacts.js';
import { attr, esc, ICON_EXTERNAL, paragraphs } from '../docsHtml.js';
import { linkFor, navLabel, parentOf, type DocsCatalogue } from '../docsModel.js';
import { bilingual, type BilingualText } from '../docsText.js';
import { chipDate, chipText } from '../docsTime.js';
import { renderEditForm, renderEditRow, renderHistory } from './renderEdit.js';

function factsHtml(facts: FactRow[]): string {
  if (facts.length === 0) return '';
  return `
      <dl class="docs-facts">
        ${facts
          .map(
            (f) =>
              `<div class="docs-fact"><dt>${esc(f.label)}</dt><dd${attr('title', f.title)}>${
                f.href
                  ? f.internal
                    ? `<a href="${esc(f.href)}" data-docs-link>${esc(f.value)}</a>`
                    : `<a href="${esc(f.href)}" target="_blank" rel="noopener noreferrer">${esc(f.value)}</a>`
                  : esc(f.value)
              }</dd></div>`
          )
          .join('')}
      </dl>`;
}

/**
 * The description in the page's language, then the other language's text as a labelled
 * block (»Česky« / »Slovinsky«). The label is chrome, so it carries the page's language
 * while the block carries the text's.
 */
export function renderDescription(t: BilingualText): string {
  const main = t.desc ? `<div class="docs-desc">${paragraphs(t.desc)}</div>` : '';
  const alt = t.altDesc
    ? `<div class="docs-desc-alt" lang="${t.altLang}"><div class="docs-desc-alt-label" lang="${getDocsLanguage()}">${esc(docsCopy.katalog.altHeading)}</div>${paragraphs(t.altDesc)}</div>`
    : '';
  return main + alt;
}

/** »česky: <Czech title>« under a Slovenian title — or »slovinsky: …« on /cz/docs. */
export function renderAltTitle(t: BilingualText): string {
  return t.altTitle
    ? `<div class="docs-title-alt">${esc(docsCopy.katalog.altTitle)}: <span lang="${t.altLang}">${esc(t.altTitle)}</span></div>`
    : '';
}

export interface SectionOptions {
  /** The section's anchor id (`sloj-<xml_id>` / `sloj-<key>`). */
  id: string;
  section: WikiSection;
  /** King-admin editing (0569); absent or `isKingAdmin: false` renders the public section. */
  edit?: EditState;
}

/** A layer the map names that the seed does not (yet): the section stands, greyed. */
export function renderMissingSection(id: string, name: string): string {
  return `
      <section id="${esc(id)}" class="docs-section docs-cad-layer docs-cad-layer--missing">
        <h2 class="docs-h2 docs-h2--sm docs-cad-layer-title">${esc(name)}</h2>
        <p class="docs-desc docs-desc--empty">${esc(docsCopy.cad.noEntry)}</p>
      </section>`;
}

export function renderEntrySection(cat: DocsCatalogue, e: WikiEntry, opts: SectionOptions): string {
  const c = docsCopy;
  const { id, section, edit } = opts;
  const admin = edit?.isKingAdmin === true;
  const editing = admin && edit.editingKey === e.key;
  const cad = section === 'cad';
  const parent = parentOf(cat, e);
  // A part without a ledger of its own is dated by its product (the DRAJV layers).
  const shown = inheritDates(e, parent);
  const text = bilingual(e, getDocsLanguage());
  const relations = buildRelations(cat, e);
  const links = buildLinks(e);
  const comps = buildComponents(e, cat.generatedAt);
  // The CAD tab already says which product serves the entry — no surface chip there.
  const surface = cad ? null : surfaceChip(e, parent);

  const entryLink = (x: WikiEntry, cls: string): string =>
    `<a class="${cls}" href="${esc(linkFor(cat, x.key))}" data-docs-link title="${esc(x.title)}">${esc(navLabel(x))}</a>`;

  const parentHtml = relations.parent
    ? `<div class="docs-parent">${esc(c.related.parent[relations.parent.kind])}: ${entryLink(relations.parent.entry, 'docs-parent-link')}</div>`
    : '';

  const descHtml = editing
    ? renderEditForm(e, edit)
    : `<div class="docs-opis">
        ${hasDescription(e) ? renderDescription(text) : `<p class="docs-desc docs-desc--empty">${esc(c.noDescription)}</p>`}
        ${admin ? renderEditRow(e) : ''}
      </div>`;

  // The full card, publisher row included — every section stands on its own.
  const own = buildFacts(shown, parent, section);

  const linksHtml =
    links.length > 0
      ? `<div class="docs-sub">
          <h3 class="docs-section-title">${esc(c.links.heading)}</h3>
          <ul class="docs-links" role="list">
            ${links
              .map(
                (l) =>
                  `<li><a class="docs-link" href="${esc(l.url)}" target="_blank" rel="noopener noreferrer">${esc(l.label)} ${ICON_EXTERNAL}</a></li>`
              )
              .join('')}
          </ul>
        </div>`
      : '';

  const relatedHtml =
    relations.children.length > 0
      ? `<div class="docs-sub docs-related">
          <h3 class="docs-section-title">${esc(c.related.heading)}</h3>
          ${relations.children
            .map(
              (g) => `<div class="docs-related-group">
                <div class="docs-related-label">${esc(c.related.children[g.kind])} · ${g.entries.length}</div>
                <ul class="docs-pills" role="list">${g.entries.map((x) => `<li>${entryLink(x, 'docs-pill')}</li>`).join('')}</ul>
              </div>`
            )
            .join('')}
        </div>`
      : '';

  const compsHtml =
    comps.length >= 2
      ? `<div class="docs-sub">
          <h3 class="docs-section-title">${esc(c.componentsHeading)}</h3>
          <ul class="docs-comps" role="list">
            ${comps
              .map(
                (x) =>
                  `<li class="docs-comp"><span class="docs-comp-label">${esc(x.label)}</span>${
                    x.meta ? `<span class="docs-comp-meta">${esc(x.meta)}</span>` : ''
                  }<span class="docs-chip">${esc(x.chip)}</span></li>`
              )
              .join('')}
          </ul>
        </div>`
      : '';

  const historyHtml = admin ? renderHistory(edit, e.key) : '';

  return `
    <section id="${esc(id)}" class="docs-section docs-cad-layer">
      <h2 class="docs-h2 docs-h2--sm docs-cad-layer-title"><a class="docs-cad-layer-anchor" href="#${esc(id)}">${esc(text.title)}</a></h2>
      <div class="docs-chips">
        <span class="docs-chip">${esc(chipText(chipDate(shown), cat.generatedAt))}</span>
        ${surface ? `<span class="docs-chip docs-chip--surface">${esc(surface)}</span>` : ''}
      </div>
      ${renderAltTitle(text)}
      ${parentHtml}
      ${descHtml}
      ${factsHtml(own)}
      ${linksHtml}
      ${relatedHtml}
      ${compsHtml}
      ${historyHtml}
    </section>`;
}

export interface PagerEnd {
  href: string;
  label: string;
}

/** The prev / next page links at the foot of a page. */
export function renderPager(prev: PagerEnd | null, next: PagerEnd | null): string {
  const c = docsCopy.katalog;
  return `
      <nav class="docs-pager" aria-label="${esc(c.prev)} / ${esc(c.next)}">
        ${
          prev
            ? `<a class="docs-pager-btn" href="${esc(prev.href)}" data-docs-link rel="prev"><span class="docs-pager-dir">${esc(c.prev)}</span><span class="docs-pager-label">${esc(prev.label)}</span></a>`
            : '<span></span>'
        }
        ${
          next
            ? `<a class="docs-pager-btn docs-pager-btn--next" href="${esc(next.href)}" data-docs-link rel="next"><span class="docs-pager-dir">${esc(c.next)}</span><span class="docs-pager-label">${esc(next.label)}</span></a>`
            : ''
        }
      </nav>`;
}
